import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Drawer, IconButton, List, ListItem, ListItemText, TextField, Typography } from '@mui/material';
import Close from '@mui/icons-material/Close';
import { useNotify, useTranslate } from 'react-admin';
import { courseStructureService, type LessonContentVersionSummary } from '../../../../services/courseStructureService';
import type { BlockLessonContent } from './types';

interface Props {
    lessonId: number;
    open: boolean;
    onClose: () => void;
    /** Called with the restored content so the editor can remount. */
    onRestored: (content: BlockLessonContent) => void;
    /** Bumps to reload the list (e.g. after a save). */
    refreshKey?: number;
}

/**
 * Version history of a block lesson (plan §6.5): newest first, restore writes a
 * `pre_restore` snapshot server-side and returns the restored content.
 */
export const LessonContentVersionPanel: React.FC<Props> = ({ lessonId, open, onClose, onRestored, refreshKey }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const [versions, setVersions] = useState<LessonContentVersionSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [restoring, setRestoring] = useState<number | null>(null);
    const [maxVersions, setMaxVersions] = useState<number | null>(null);
    const [comment, setComment] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const history = await courseStructureService.getVersionHistory(lessonId);
            setVersions(history.versions);
            setMaxVersions(history.maxVersions);
        } catch {
            notify('blockLesson.versions.loadError', { type: 'error' });
        } finally {
            setLoading(false);
        }
    }, [lessonId, notify]);

    useEffect(() => {
        if (open) void load();
    }, [open, load, refreshKey]);

    const saveNamed = async () => {
        setSaving(true);
        try {
            await courseStructureService.createVersion(lessonId, comment.trim());
            setComment('');
            notify('blockLesson.versions.saved', { type: 'success' });
            await load();
        } catch {
            notify('blockLesson.versions.saveError', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const restore = async (versionNumber: number) => {
        if (!window.confirm(translate('blockLesson.versions.confirmRestore', { version: versionNumber }))) return;
        setRestoring(versionNumber);
        try {
            const result = await courseStructureService.restoreVersion(lessonId, versionNumber);
            onRestored(result.content);
            notify('blockLesson.versions.restored', { type: 'success', messageArgs: { version: versionNumber } });
            await load();
        } catch {
            notify('blockLesson.versions.restoreError', { type: 'error' });
        } finally {
            setRestoring(null);
        }
    };

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid var(--oa-divider)' }}>
                <Typography variant="h6">{translate('blockLesson.versions.title')}</Typography>
                <IconButton onClick={onClose} aria-label={translate('ra.action.close')}><Close /></IconButton>
            </Box>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid var(--oa-divider)', display: 'flex', gap: 1 }}>
                <TextField size="small" fullWidth value={comment} onChange={(e) => setComment(e.target.value)} placeholder={translate('blockLesson.versions.commentPlaceholder')} inputProps={{ maxLength: 255, 'aria-label': translate('blockLesson.versions.commentPlaceholder') }} />
                <Button variant="outlined" disabled={saving} onClick={() => void saveNamed()} sx={{ whiteSpace: 'nowrap' }}>
                    {translate('blockLesson.versions.saveNamed')}
                </Button>
            </Box>
            {maxVersions !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1 }} data-testid="versions-retention">
                    {translate('blockLesson.versions.retention', { max: maxVersions })}
                </Typography>
            )}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : versions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>{translate('blockLesson.versions.empty')}</Typography>
            ) : (
                <List dense>
                    {versions.map((v) => (
                        <ListItem
                            key={v.id}
                            divider
                            secondaryAction={
                                <Button size="small" variant="outlined" disabled={restoring !== null} onClick={() => restore(v.versionNumber)}>
                                    {restoring === v.versionNumber ? <CircularProgress size={14} /> : translate('blockLesson.versions.restore')}
                                </Button>
                            }
                        >
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <span>{translate('blockLesson.versions.version', { version: v.versionNumber })}</span>
                                        <Chip size="small" label={translate(`blockLesson.versions.source.${v.source}`)} />
                                    </Box>
                                }
                                secondary={`${new Date(v.createdAt).toLocaleString()} · ${translate('blockLesson.versions.blocks', { smart_count: v.blockCount })}${v.comment ? ` · ${v.comment}` : ''}`}
                            />
                        </ListItem>
                    ))}
                </List>
            )}
        </Drawer>
    );
};
